'use client';

import { c as _c } from "react/compiler-runtime";
import { useCallback, useRef } from 'react';
import type { DocumentoPadrao, RecusaDoUsoDocumentoPadrao } from '../types/documentosPadraoTypes';

/**
 * Sem resposta, 5xx ou falha que nem é recusa traduzida: o core pode ter gravado, e a chave fica.
 * Só um 4xx é recusa definitiva.
 */
const podeTerGravadoNoServidor = (erro: unknown): boolean => {
  const status = (erro as Partial<RecusaDoUsoDocumentoPadrao> | null)?.status;
  return typeof status !== 'number' || status >= 500;
};

/**
 * O `onConfirmar` do painel "Buscar documento padrão", com a chave idempotente (`clientRequestId`)
 * que os dois destinos do uso exigem: os autos do processo e o rascunho do cadastro.
 *
 * A chave é UMA por alvo enquanto a tela está aberta, e só é trocada quando a tentativa terminou de
 * forma definitiva. Uma falha de rede pode ter gravado no servidor sem a resposta chegar: repetir
 * com a MESMA chave devolve o que já existe, em vez de esbarrar em "já está" com uma chave nova. O
 * alvo é `escopo` + documento + versão; o cadastro põe o destino no escopo, porque a mesma versão
 * em outro destino é outro ato (e a mesma chave nele seria recusada).
 *
 * `usar` recebe a chave e recusa com `RecusaDoUsoDocumentoPadrao`.
 *
 * @example const confirmar = useUsoDocumentoPadraoComChave(processoId, (doc, chave) => juntar(doc, chave))
 */
export function useUsoDocumentoPadraoComChave(escopo, usar) {
  const $ = _c(4);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = new Map();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const chaves = useRef(t0);
  let t1;
  if ($[1] !== escopo || $[2] !== usar) {
    t1 = async documento => {
      const alvo = `${escopo}:${documento.id}:${documento.versaoAtual}`;
      const clientRequestId = chaves.current.get(alvo) ?? crypto.randomUUID();
      chaves.current.set(alvo, clientRequestId);
      ;
      try {
        await usar(documento, clientRequestId);
      } catch (t2) {
        const erro = t2;
        if (!podeTerGravadoNoServidor(erro)) {
          chaves.current.delete(alvo);
        }
        throw erro;
      }
      chaves.current.delete(alvo);
    };
    $[1] = escopo;
    $[2] = usar;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
}